import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Alert from "../../components/Alert";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { createLookupApi } from "../../api/lookupApi";
import { listWeeklyHolidays, createWeeklyHoliday, deleteWeeklyHoliday } from "../../api/attendanceApi";
import "../Users/UsersPage.css";
import "./Holiday.css";

const academicYearApi = createLookupApi("academic-years");

/** Real fixed 7-option list, direct port of the old app's own `<select id="dayName">` in weekly-holiday.html. */
const DAY_NAMES = ["Saturday", "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

/**
 * Direct port of digicampusbd's `weekly-holiday.html` /
 * `WeeklyHoliudayService.holidayWeeklySaveDetails`. Found while sweeping the
 * Holiday module (see MIGRATION.md): despite its name, this page previously
 * behaved like a second "Others Holiday" — pick one arbitrary date + a free
 * text name. The real feature is a recurring-weekday generator: pick an
 * Academic Year + a Day of the week + Sections, and the real backend expands
 * that into every calendar date in that year landing on that weekday (e.g.
 * "Friday" + 2026 -> every Friday in 2026), creating one `WeeklyHoliday` row
 * per date, all mapped to the same sections. Rebuilt this page and the
 * backend's `createWeeklyHoliday` to match that real shape.
 */
export default function WeeklyHolidayPage() {
  const [holidays, setHolidays] = useState([]);
  const [classConfigurations, setClassConfigurations] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({ academicYear: "", dayName: "", classConfigurationIds: [] });

  const load = () => {
    setLoading(true);
    Promise.all([listWeeklyHolidays(), listClassConfigurations(), academicYearApi.list()])
      .then(([holidayList, classList, years]) => {
        setHolidays(holidayList);
        setClassConfigurations(classList);
        setAcademicYears(years);
      })
      .catch(() => setError("Failed to load weekly holidays."))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const toggleClass = (id) => {
    setForm((f) => ({
      ...f,
      classConfigurationIds: f.classConfigurationIds.includes(id)
        ? f.classConfigurationIds.filter((c) => c !== id)
        : [...f.classConfigurationIds, id],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const created = await createWeeklyHoliday(form);
      setSuccess(`Weekly holiday created for ${created.length} ${form.dayName}(s) in ${form.academicYear}.`);
      setForm({ academicYear: "", dayName: "", classConfigurationIds: [] });
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to create.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete holiday "${row.date}"?`)) return;
    setError("");
    try {
      await deleteWeeklyHoliday(row.id);
      setSuccess("Deleted.");
      setHolidays((prev) => prev.filter((h) => h.id !== row.id));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete.");
    }
  };

  const columns = [
    { key: "date", label: "Date" },
    { key: "name", label: "Day" },
    { key: "academicYear", label: "Academic Year" },
    { key: "classConfigNames", label: "Classes", render: (row) => row.classConfigNames.join(", ") },
  ];

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Weekly Holiday</h1>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      <form onSubmit={handleSubmit} className="user-form" style={{ marginBottom: 24 }}>
        <label>Select Year *</label>
        <select value={form.academicYear} onChange={(e) => setForm({ ...form, academicYear: e.target.value })} required>
          <option value="" disabled>
            Select Academic Year
          </option>
          {academicYears.map((y) => (
            <option key={y.id} value={y.name}>
              {y.name}
            </option>
          ))}
        </select>
        <label>Select Day *</label>
        <select value={form.dayName} onChange={(e) => setForm({ ...form, dayName: e.target.value })} required>
          <option value="" disabled>
            Select Day
          </option>
          {DAY_NAMES.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
        <label>Select Section *</label>
        <div className="checkbox-grid">
          {classConfigurations.map((c) => (
            <label key={c.id}>
              <input
                type="checkbox"
                checked={form.classConfigurationIds.includes(c.id)}
                onChange={() => toggleClass(c.id)}
              />
              <span>{c.classConfigName}</span>
            </label>
          ))}
        </div>
        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={saving || form.classConfigurationIds.length === 0}>
            {saving ? "Saving..." : "+ Add Weekly Holiday"}
          </button>
        </div>
      </form>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={columns}
          rows={holidays}
          actions={(row) => (
            <button type="button" className="btn-link danger" onClick={() => handleDelete(row)}>
              Delete
            </button>
          )}
        />
      )}
    </MainLayout>
  );
}
